import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import {
  GetParticipationsByEventShiftCommandOutput,
  GetPeopleCommandOutput,
  Shift,
} from '@amzn/red-velvet-api';
import { useParticipantsFilterProperties } from '../../components/attendance/wizard/tablePropertyFilter';
import {
  getAllPagesRedVelvetQueryOptions,
  useAllPagesRedVelvetQuery,
} from '../allPagesRedVelvetQuery';
import { useUser } from '../user';
import { useAttendeeSessionStorage } from './attendeeSessionStorage';
import { useParticipantSessionStorage } from './participantSessionStorage';
import { useBulkParticipantSessionStorage } from './bulkParticipantSessionStorage';
import { useQueries } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { featureIsEnabled, getEnabledFeatures, useFeatures } from '../features';
import { useShiftTime } from '../shifts/shiftTime';
import {
  Amazonian,
  getAmazoniansFromParticipants,
  getAmazoniansFromPeople,
} from '../../utils/attendance/peopleSearch';
import { formatShiftDate, formatShiftTime } from '../../utils/dateFormatting';
import {
  TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT,
  TRACK_ATTENDANCE_BULK_UPLOAD_MULTI_SHIFT,
} from '../../utils/constants';

function getSignedUpOrAttendedParticipants(
  urlAttendees: Amazonian[],
  apiParticipants: Amazonian[],
  showPending = false
) {
  const userMap: { [key: string]: Amazonian } = {};
  apiParticipants.forEach((participant) => {
    // Only show signed up or attended participants
    if (
      (participant.signedUp && (showPending || participant.attendance)) ||
      participant.attendance === 'ATTENDED'
    ) {
      userMap[participant.alias || ''] = participant;
    }
  });

  urlAttendees.forEach((participant) => {
    // De-dupe url and api participants
    if (!userMap[participant.alias || '']) {
      userMap[participant.alias || ''] = participant;
    }
  });

  return (
    Object.values(userMap)
      // PAPI sends undefined and NO_ALIAS data when a user has left the company
      // This causes confusing table entries on the front end, and errors in our API
      // so we are filtering them out for now
      .filter((participant) => participant.alias && participant.alias !== 'NO_ALIAS')
  );
}

// PAPI has a limit of 25 items for alias search to prevent timeouts
// See: https://t.corp.amazon.com/V1682060082/communication
const PAPI_ALIAS_CHUNK_SIZE = 25;

function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

export function useParticipants(
  namespaces: string[],
  tableName: string,
  eventId: string,
  shiftId: string,
  pageSize: number,
  showPending = false,
  shifts?: Shift[],
  vcCreated?: boolean
) {
  const { aliases } = useAttendeeSessionStorage();
  const { t } = useTranslation(namespaces);
  const user = useUser();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isBulkUploadSingleShiftEnabled = featureIsEnabled(
    TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT,
    features,
    searchParams
  );
  const isBulkUploadMultiShiftEnabled = featureIsEnabled(
    TRACK_ATTENDANCE_BULK_UPLOAD_MULTI_SHIFT,
    features,
    searchParams
  );

  const isEventLevelTracking = eventId && !shiftId && isBulkUploadMultiShiftEnabled;

  // Create a map of shiftId -> Shift for quick lookup (used for event-level tracking)
  const shiftMap = React.useMemo(() => {
    const map = new Map<string, Shift>();
    (shifts || []).forEach((shift) => {
      if (shift.shiftId) {
        map.set(shift.shiftId, shift);
      }
    });
    return map;
  }, [shifts]);

  const shiftMins = useShiftTime(eventId, shiftId, vcCreated);

  // Deduplicate aliases from URL before chunking
  const uniqueAliases = Array.from(new Set(aliases));

  // Chunk aliases to respect PAPI's 25-item limit
  const aliasChunks =
    uniqueAliases.length > 0 ? chunkArray(uniqueAliases, PAPI_ALIAS_CHUNK_SIZE) : [];

  // Query each chunk separately using useQueries (designed for dynamic number of queries)
  const chunkQueries = useQueries({
    queries: aliasChunks.map((chunk) => {
      const options = getAllPagesRedVelvetQueryOptions(
        {
          redVelvetQueryKey: ['getPeople', { aliases: chunk.join(',') }],
          converter: (output: GetPeopleCommandOutput) => ({
            items: output.people || [],
            next: output.page,
          }),
        },
        getEnabledFeatures(features, searchParams)
      );
      return {
        ...options,
        enabled: options?.enabled ?? true,
      };
    }),
  });

  // Combine results from all chunks
  const rawPeople = chunkQueries.flatMap((query) => query.data || []);
  const isLoadingPeople = chunkQueries.some((query) => query.isLoading);
  const isErrorPeople = chunkQueries.some((query) => query.isError);

  // Get all pages so we can de-deupe with attendees
  // Do NOT query this if there is no shiftId or eventId, because that indicates
  // we are in the process of creating the shift, and possibly the event as well
  // For event-level tracking (multi-shift), we query with eventId only.
  const participantResult = useAllPagesRedVelvetQuery({
    enabled: eventId !== '' && (shiftId !== '' || Boolean(isEventLevelTracking)),
    redVelvetQueryKey: ['getParticipationsByEventShift', { eventId, shiftId }],
    converter: (output: GetParticipationsByEventShiftCommandOutput) => {
      const items = (output.participations || []).map((p) => ({
        ...p,
      }));
      return { items, next: output.page };
    },
  });

  const rawParticipants = participantResult.data || [];

  const { participants: participantsUrlData } = useParticipantSessionStorage();
  const { bulkParticipants } = useBulkParticipantSessionStorage();

  /*
    New logic: 
     - Get event's participation data from getParticipationsByEventShift 
     - Merge with Attendees modal's participation data from getParticipationsByEventAndAttendees,
     - Finally, apply user manual changes from participantsUrlData
  */
  let bulkParticipantsAndShiftParticipants: Amazonian[] = [];
  if (isBulkUploadSingleShiftEnabled || isEventLevelTracking) {
    // Get initial event's participants data from getParticipationsByEventShift
    bulkParticipantsAndShiftParticipants = getAmazoniansFromParticipants(rawParticipants, {});

    // For event-level tracking, map each participant's shiftId to its data
    if (isEventLevelTracking) {
      bulkParticipantsAndShiftParticipants = bulkParticipantsAndShiftParticipants.reduce<
        Amazonian[]
      >((filteredParticipants, participant) => {
        const shift = participant.shiftId ? shiftMap.get(participant.shiftId) : undefined;
        if (shift?.name) {
          filteredParticipants.push({
            ...participant,
            shiftName: shift.name,
            shiftDate: formatShiftDate(shift.startDateTime),
            shiftTime: formatShiftTime(shift.startDateTime),
          });
        }
        return filteredParticipants;
      }, []);
    }

    // Merge with bulkParticipants from Add attendees modal
    Object.entries(bulkParticipants).forEach(([key, data]) => {
      // For event-level tracking, match using alias and originalShiftId (to find the original row)
      const findIndex = isEventLevelTracking
        ? bulkParticipantsAndShiftParticipants.findIndex(
            (p) => p.alias === data.alias && p.shiftId === data.originalShiftId
          )
        : bulkParticipantsAndShiftParticipants.findIndex((p) => p.alias === key);

      if (findIndex >= 0) {
        bulkParticipantsAndShiftParticipants[findIndex] = {
          ...bulkParticipantsAndShiftParticipants[findIndex],
          ...data,
        };
      } else {
        // Add new participant, enriching with shift data for event-level tracking
        if (isEventLevelTracking && data.shiftId) {
          const shift = shiftMap.get(data.shiftId);
          // Only add if shift has a name
          if (shift?.name) {
            bulkParticipantsAndShiftParticipants.push({
              ...data,
              shiftName: shift.name,
              shiftDate: formatShiftDate(shift?.startDateTime),
              shiftTime: formatShiftTime(shift?.startDateTime),
            });
          }
        } else {
          bulkParticipantsAndShiftParticipants.push(data);
        }
      }
    });

    // Apply participantsUrlData from user manual changes with highest priority
    bulkParticipantsAndShiftParticipants.forEach((participant, index) => {
      if (participant.alias && participantsUrlData[participant.alias]) {
        bulkParticipantsAndShiftParticipants[index] = {
          ...participant,
          ...participantsUrlData[participant.alias],
        };
      }
    });
  }

  /*
  Existing logic: Get initial participants data then merge with PAPI searched people
  */
  const participants = getAmazoniansFromParticipants(rawParticipants, participantsUrlData);
  const people =
    isBulkUploadSingleShiftEnabled || isEventLevelTracking
      ? []
      : getAmazoniansFromPeople(rawPeople, user.userId, shiftMins, participantsUrlData);
  const signedUpOrAttendedParticipants =
    isBulkUploadSingleShiftEnabled || isEventLevelTracking
      ? []
      : getSignedUpOrAttendedParticipants(people, participants, showPending);

  const useBulkUploadFlow = isBulkUploadSingleShiftEnabled || isEventLevelTracking;

  const filteringProperties = useParticipantsFilterProperties();
  const collectionData = useCollection<Amazonian>(
    useBulkUploadFlow ? bulkParticipantsAndShiftParticipants : signedUpOrAttendedParticipants,
    {
      filtering: {
        empty: (
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <b>
              {participantResult.isError || isErrorPeople
                ? t(`${tableName}.errorOccurred`)
                : t(`${tableName}.noneFound`)}
            </b>
          </Box>
        ),
      },
      propertyFiltering: {
        filteringProperties,
      },
      pagination: { pageSize: pageSize },
      sorting: {},
      selection: {
        // We should use alias (not name) as the unique identifier for selection tracking.
        // Aliases are -almost- guaranteed unique, but names can certainly have duplicates.
        // Without trackBy, useCollection uses object reference equality which breaks when new objects
        // are created on each render, causing selection state issues and items duplicating across pages.
        // For event-level tracking, use composite key (alias + shiftId) since same person can appear multiple times.
        trackBy: isEventLevelTracking
          ? (item) => `${item.alias}-${item.shiftId}`
          : (item) => item.alias || '',
        keepSelection: true,
      },
    }
  );

  return {
    isError: participantResult.isError || isErrorPeople,
    isLoading: participantResult.isLoading || isLoadingPeople,
    participants: useBulkUploadFlow
      ? bulkParticipantsAndShiftParticipants
      : signedUpOrAttendedParticipants,
    rawParticipants,
    ...collectionData,
  };
}
